import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Wallet, TrendingDown, PoundSterling, PiggyBank } from 'lucide-react';
import { api } from '@/lib/api';
import { KpiCard } from '@/components/KpiCard';
import { TodayCard } from '@/components/TodayCard';
import { OpenRiskStrip } from '@/components/OpenRiskStrip';
import { TiltBanner } from '@/components/TiltBanner';
import { EquityCurve } from '@/components/EquityCurve';
import { AllocationTreemap } from '@/components/AllocationTreemap';
import { ConcentrationCard } from '@/components/ConcentrationCard';
import { ViolationsPanel } from '@/components/ViolationsPanel';
import { WatchlistCard } from '@/components/WatchlistCard';
import { EarningsStrip } from '@/components/EarningsStrip';
import { MacroStrip } from '@/widgets/MacroStrip';
import { fmtMoney, fmtPercent, fmtSignedMoney, fmtSignedPercent } from '@/lib/format';

export const Route = createFileRoute('/')({
  component: HomePage,
});

function HomePage() {
  const summary = useQuery({
    queryKey: ['summary'],
    queryFn: api.accountSummary,
    refetchInterval: 30_000,
  });
  const positions = useQuery({
    queryKey: ['positions'],
    queryFn: api.positions,
    refetchInterval: 30_000,
  });
  const snapshots = useQuery({
    queryKey: ['snapshots'],
    queryFn: api.snapshots,
    refetchInterval: 60_000,
  });
  const violations = useQuery({
    queryKey: ['violations'],
    queryFn: api.violations,
    refetchInterval: 30_000,
  });
  const transactions = useQuery({
    queryKey: ['transactions'],
    queryFn: () => api.transactionHistory(),
  });
  const activeOrders = useQuery({
    queryKey: ['active-orders'],
    queryFn: api.activeOrders,
    refetchInterval: 30_000,
  });

  const totalValue = summary.data?.totalValue ?? 0;
  const cashPct =
    summary.data && totalValue > 0
      ? (summary.data.cash.availableToTrade / totalValue) * 100
      : 0;
  const unrealised = summary.data?.investments.unrealizedProfitLoss ?? 0;
  const realized = summary.data?.investments.realizedProfitLoss ?? 0;
  const netDeposits =
    transactions.data?.items
      .filter((t) => t.type === 'DEPOSIT' || t.type === 'WITHDRAWAL')
      .reduce((acc, t) => acc + t.amount, 0) ?? 0;
  const truePnl = totalValue - netDeposits;
  const truePnlPct = netDeposits > 0 ? (truePnl / netDeposits) * 100 : 0;

  const snaps = snapshots.data?.snapshots ?? [];
  let peak = 0;
  let maxDrawdown = 0;
  for (const s of snaps) {
    peak = Math.max(peak, s.totalValue);
    if (peak > 0) {
      const dd = ((peak - s.totalValue) / peak) * 100;
      maxDrawdown = Math.max(maxDrawdown, dd);
    }
  }
  const currentDrawdown =
    peak > 0 ? ((peak - totalValue) / peak) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-6"
    >
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 tracking-tight">Dashboard</h1>
          <p className="text-[12px] text-zinc-400 mt-0.5">
            Live snapshot of your account, polled every 30 seconds.
          </p>
        </div>
      </div>

      <TodayCard
        positions={positions.data ?? []}
        snapshots={snaps}
        violations={violations.data?.violations ?? []}
      />

      <OpenRiskStrip
        positions={positions.data ?? []}
        activeOrders={activeOrders.data ?? []}
      />

      <TiltBanner />

      <MacroStrip />
      <EarningsStrip />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total value"
          value={totalValue}
          format={(v) => fmtMoney(v)}
          icon={Wallet}
          accent="indigo"
          highlight
          hint={summary.data ? `Account ${summary.data.currency}` : undefined}
        />
        <KpiCard
          label="True P&L"
          value={truePnl}
          format={(v) => fmtSignedMoney(v)}
          delta={truePnlPct}
          deltaFormat={(v) => fmtSignedPercent(v)}
          icon={PoundSterling}
          accent={truePnl >= 0 ? 'emerald' : 'rose'}
          hint="Account value minus net deposits"
        />
        <KpiCard
          label="Drawdown"
          value={currentDrawdown}
          format={(v) => fmtPercent(v)}
          icon={TrendingDown}
          accent={currentDrawdown > 5 ? 'rose' : 'amber'}
          highlight={currentDrawdown > 10}
          hint={`Max ${fmtPercent(maxDrawdown)} since first snapshot`}
        />
        <KpiCard
          label="Cash reserve"
          value={cashPct}
          format={(v) => fmtPercent(v)}
          icon={PiggyBank}
          accent="indigo"
          hint={summary.data ? fmtMoney(summary.data.cash.availableToTrade) : undefined}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <EquityCurve snapshots={snaps} />
          <AllocationTreemap positions={positions.data ?? []} />
        </div>
        <div className="space-y-4">
          <ViolationsPanel violations={violations.data?.violations ?? []} />
          <ConcentrationCard
            positions={positions.data ?? []}
            totalValue={totalValue}
          />
          <WatchlistCard />
        </div>
      </div>

      <div className="text-[10px] text-zinc-400 pt-2 text-right">
        Unrealised {fmtSignedMoney(unrealised)} · Realised {fmtSignedMoney(realized)}
      </div>
    </motion.div>
  );
}
